import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useToggle } from 'react-use';
import { v4 } from 'uuid';
import { useSearchDiagnosticPackagesQuery, useSearchDiagnosticPackageActionsQuery } from 'apis/dl';
import { ReceiveIcon } from 'components/icons';
import Button from 'components/shared/Button';
import Dialog from 'components/shared/Dialog';
import { DiagnosticPackageRetrieveDialog, DiagnosticPackageTable } from './components';
import { canDownloadDiagnosticPackages, canRetrieveDiagnosticPackages } from './utils';
import styles from './DiagnosticPackageModal.module.css';

function DiagnosticPackageModal({ deviceId, onClose }) {
  const titleId = useMemo(() => v4(), []);
  const permissions = useSelector((store) => store.data?.rolePermissions?.permissions);
  const [retrieveDialogOpened, toggleRetrieveDialogOpened] = useToggle(false);
  const [activeDiagPkgId, setActiveDiagPkgId] = useState(null);
  const packagesQuery = useSearchDiagnosticPackagesQuery({ deviceIds: [deviceId] }, { placeholderData: [] });
  const { data, error, refetch } = packagesQuery;
  const actionsQuery = useSearchDiagnosticPackageActionsQuery(
    { deviceId, status: 'active' },
    { enabled: !activeDiagPkgId, placeholderData: [], refetchInterval: 15 * 1000 }
  );
  const { data: actions } = actionsQuery;
  const openRetrieveDialog = useCallback(() => {
    toggleRetrieveDialogOpened(true);
  }, [toggleRetrieveDialogOpened]);
  const closeRetrieveDialog = useCallback(() => {
    toggleRetrieveDialogOpened(false);
  }, [toggleRetrieveDialogOpened]);
  const handleRetrieve = useCallback((diagPkgId) => {
    setActiveDiagPkgId(diagPkgId);
  }, []);
  const handleReceive = useCallback(() => {
    setActiveDiagPkgId(null);
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (!actions || actions.length === 0) {
      return;
    }

    const oldestActiveDiagPkg = actions[actions.length - 1];

    setActiveDiagPkgId(oldestActiveDiagPkg.commissioningId);
  }, [actions]);

  const retrieveDisabled =
    !canRetrieveDiagnosticPackages(permissions) ||
    actionsQuery.isFetching ||
    actionsQuery.isError ||
    activeDiagPkgId != null;

  return (
    <Fragment>
      <Dialog
        aria-labelledby={titleId}
        aria-busy={packagesQuery.isFetching || actionsQuery.isFetching}
        open={true}
        onClose={onClose}
      >
        <Dialog.Container maxWidth={540}>
          <Dialog.Title>
            <h3 id={titleId} className={styles.heading}>
              Diagnostic Logs
            </h3>
          </Dialog.Title>
          {packagesQuery.isError ? (
            <Dialog.Content>
              <p>Error: {error.message}</p>
            </Dialog.Content>
          ) : (
            <DiagnosticPackageTable
              data={data}
              loading={packagesQuery.isFetching}
              downloadable={canDownloadDiagnosticPackages(permissions)}
              retrievingId={activeDiagPkgId}
              retrieving={actionsQuery.isFetching}
              onComplete={handleReceive}
              onRetry={handleRetrieve}
            />
          )}
          <Dialog.Actions className={styles.actions}>
            <Button
              variant="text"
              color="primary"
              size="medium"
              disabled={retrieveDisabled}
              onClick={openRetrieveDialog}
              start={<ReceiveIcon size="medium" />}
            >
              Retrieve the latest Diagnostic Package
            </Button>
          </Dialog.Actions>
          <Dialog.Close />
        </Dialog.Container>
      </Dialog>
      {retrieveDialogOpened && (
        <DiagnosticPackageRetrieveDialog
          deviceId={deviceId}
          onClose={closeRetrieveDialog}
          onRetrieve={handleRetrieve}
        />
      )}
    </Fragment>
  );
}

export default DiagnosticPackageModal;
